import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { SubscriptionBanner } from '@/components/OtherMenu/SubscriptionBanner';
import { Cell } from '@/components/ProfileMenu/Cell';
import { useProfileMenu } from '@/components/ProfileMenu/ProfileMenuProvider';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useTheme } from '@/hooks/useTheme';
import { IconType, ProfileMenuView } from '@/types/components';
import {
  ROUTE_OTHER_THEME,
  ROUTE_PARTNERS,
  ROUTE_SCHOOL_LINKS,
  ROUTE_SUBSCRIPTION,
  ROUTE_SUPPORT,
} from '@/router/routes';
import type { Theme } from '@/types/theme';

export const themeIconTypes: Record<Theme, IconType> = {
  light: IconType.ThemeLight,
  dark: IconType.ThemeDark,
  contrast: IconType.ThemeContrast,
  system: IconType.ThemeSystem,
};

const divider = <div className="mx-3 h-px bg-divider" />;

export const OtherMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open: openProfileMenu } = useProfileMenu();
  const { isRestricted } = useProfileRestriction();
  const hasSubscription = useHasFamilySubscription();
  const { theme } = useTheme();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const hasSubscriptionBanner = !hasSubscription && !isRestricted;

  return (
    <div className="flex flex-col gap-4 px-4 pt-1 pb-6">
      <div className="flex flex-col rounded-xl bg-card shadow-card">
        <Cell
          icon={IconType.Person}
          title={t('profileMenu.title')}
          onClick={() => openProfileMenu()}
        />
      </div>

      <div className="flex flex-col rounded-xl bg-card shadow-card">
        <Cell
          icon={IconType.Partners}
          title={t('sidebar.tabs.partners')}
          onClick={() => navigate(ROUTE_PARTNERS)}
        />
        {divider}
        <Cell
          icon={IconType.Link}
          title={t('sidebar.tabs.schoolLinks')}
          onClick={() => navigate(ROUTE_SCHOOL_LINKS)}
        />
        {divider}
        <Cell
          icon={IconType.Bell}
          title={t('sidebar.tabs.notifications')}
          onClick={() => openProfileMenu(ProfileMenuView.Notifications)}
        />
        {divider}
        {!isRestricted && (
          <>
            <Cell
              icon={IconType.Subscription}
              title={t('sidebar.tabs.subscription')}
              onClick={() => navigate(ROUTE_SUBSCRIPTION)}
            />
            {divider}
          </>
        )}
        <Cell
          icon={IconType.Help}
          title={t('global.help')}
          onClick={() => navigate(ROUTE_SUPPORT)}
        />
        {divider}
        <Cell
          hasPaddedIcon
          icon={themeIconTypes[theme]}
          title={t('sidebar.tabs.themeSwitcher')}
          onClick={() => navigate(ROUTE_OTHER_THEME)}
        />
        {divider}
        <Cell
          icon={IconType.Logout}
          title={t('global.logout')}
          onClick={() => setIsLogoutConfirmOpen(true)}
        />
      </div>

      <LogoutConfirmDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
      />

      {hasSubscriptionBanner && <SubscriptionBanner />}
    </div>
  );
};
